import { I18n } from '@calendly/i18n'
import {
  AiNotetakerNoRecapDuotoneIcon,
  AiNotetakerErrorDuotoneIcon,
} from '@calendly/ui/components/icons'
import { Layout } from '@calendly/ui/components/layout'
import { Tooltip } from '@calendly/ui/components/tooltip'
import { LabelText } from '@calendly/ui/components/typography'
import {
  colorTextSecondary,
  colorTextDestructive,
  colorTextDisabled,
} from '@calendly/ui/theme'
import React, { ComponentType, useState, useCallback, useMemo } from 'react'

import { getLocationKindDisplayName } from 'features/meeting_intelligence'

import { TRACKING_EVENT_NAMES, useTrack } from '../../../hooks/analytics_events'

const COLOR_MAP = {
  disabled: colorTextDisabled,
  destructive: colorTextDestructive,
  secondary: colorTextSecondary,
} as const

type RecapBannerVariant = 'no-recap' | 'error' | 'processing'

interface RecapBannerProps {
  /**
   * The variant of the recap banner to display
   */
  variant: RecapBannerVariant

  /**
   * The i18n key suffix for the tooltip text (only used for 'no-recap' variant).
   * Will be interpolated as: `publisher.meeting_intelligence.scheduled_event.no_recap_available_tooltip.${tooltipKey}`
   */
  tooltipKey?: string

  /**
   * The event object (required for host_recording_permission_denied to interpolate provider name)
   */
  event?: any
}

interface BannerConfig {
  icon?: ComponentType
  textKey: string
  textColor: 'disabled' | 'destructive' | 'secondary'
  hasTooltip: boolean
}

const VARIANT_CONFIG: Record<RecapBannerVariant, BannerConfig> = {
  'no-recap': {
    icon: AiNotetakerNoRecapDuotoneIcon,
    textKey: 'publisher.meeting_intelligence.scheduled_event.no_recap_available',
    textColor: 'disabled',
    hasTooltip: true,
  },
  error: {
    icon: AiNotetakerErrorDuotoneIcon,
    textKey: 'publisher.meeting_intelligence.scheduled_event.recap_error',
    textColor: 'destructive',
    hasTooltip: false,
  },
  processing: {
    textKey: 'publisher.meeting_intelligence.scheduled_event.recap_processing',
    textColor: 'secondary',
    hasTooltip: false,
  },
}

/**
 * RecapBanner Component
 *
 * A unified component for displaying recap status indicators.
 * Supports multiple variants with config-based rendering.
 *
 * Variants:
 * - 'no-recap': Shows when no recap is available (with tooltip explaining why)
 * - 'error': Shows when recap generation failed
 * - 'processing': Shows when recap is being generated
 */
export function RecapBanner({ variant, tooltipKey, event }: RecapBannerProps) {
  const track = useTrack()
  const [hasTracked, setHasTracked] = useState(false)
  const config = VARIANT_CONFIG[variant]

  const handleTooltipHover = useCallback(() => {
    if (!hasTracked && tooltipKey) {
      track({
        name: TRACKING_EVENT_NAMES.TOOLTIP_CLICKED,
        properties: {
          tooltip_name: 'no_recap_available_icon',
          tooltip_location: 'meetings_page',
          tooltip_details: tooltipKey,
          action: 'hover',
        },
      })
      setHasTracked(true)
    }
  }, [hasTracked, tooltipKey, track])

  const content = useMemo(
    () => (
      <Layout direction="row" gap="8" align="center">
        {config.icon && <config.icon />}
        <LabelText size="large" color={COLOR_MAP[config.textColor]}>
          {I18n.t(config.textKey)}
        </LabelText>
      </Layout>
    ),
    [config]
  )

  const tooltipText = useMemo(
    () => {
      if (!tooltipKey) return undefined

      // Special case: host_recording_permission_denied requires provider interpolation
      if (tooltipKey === 'host_recording_permission_denied') {
        return I18n.t(
          'publisher.meeting_intelligence.scheduled_event.no_recap_available_tooltip.host_recording_permission_denied',
          {
            provider: getLocationKindDisplayName(event) || 'the meeting'
          }
        )
      }

      return I18n.t(
        `publisher.meeting_intelligence.scheduled_event.no_recap_available_tooltip.${tooltipKey}`
      )
    },
    [tooltipKey, event]
  )

  if (config.hasTooltip && tooltipText) {
    return (
      <Tooltip text={tooltipText}>
        <div onMouseEnter={handleTooltipHover} id="no-recap-tooltip-hover">
          {content}
        </div>
      </Tooltip>
    )
  }

  return content
}
